import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Palette, Zap, Flame, Shuffle, Target } from "lucide-react";
import { User } from "./HeatedGame";
import { COLOR_BLUE, COLOR_GREEN, COLOR_PURPLE, COLOR_YELLOW } from '../utils/colors';
import './css/modernCardActionModal.css';

interface CardActionModalProps {
  users: User[];
  currentUser: User;
  show: boolean;
  onClose: Function;
  type: "ghostPepper" | "fairPlay" | "gettingHeated" | "apocalypse" | "colorPicker" | "swap" | "stackingTarget";
}

const ModernCardActionModal: React.FC<CardActionModalProps> = ({ users, currentUser, show, onClose, type }) => {

  const drawOptions = (): any => {
    let emptyDrawSelections: { [playerId: string]: number } = {};
    for (let i = 0; i < users.length; i++) {
      if (users[i].id !== currentUser.id) {
        emptyDrawSelections[users[i].id] = 0
      }
    }
    return emptyDrawSelections;
  }

  const [selectedId, setSelectedId] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedPlayerButton, setSelectedPlayerButton] = useState(-1);
  const [userDrawSelections, setUserDrawSelections] = useState<{ [playerId: string]: number }>(drawOptions());
  const [nextDrawCountOption, setNextDrawCountOption] = useState(1);

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const onSet = () => {
    if (type === "apocalypse" || type === "colorPicker") {
      onSetColor();
    } else if (type === "ghostPepper") {
      onSetGhostPepper();
    } else if (type === "fairPlay") {
      onSetFairPlay();
    } else if (type === "gettingHeated") {
      onSetGettingHeated();
    } else if (type === "stackingTarget") {
      onSetGettingHeated(); // Same behavior as gettingHeated
    } else if (type === "swap"){
      onSetSwap()
    }
  }

  const onSetColor = () => {
    onClose(selectedColor);
  };

  const onSetGhostPepper = () => {
    onClose(userDrawSelections, selectedColor);
  }

  const onSetGettingHeated = () => {
    onClose(selectedId, selectedColor);
  }

  const onSetSwap = () => {
    onClose(selectedId);
  }

  const onSetFairPlay = () => {
    const selectedUser: User | undefined = users.find((user) => user.id === selectedId);
    const currentPlayer: User | undefined = users.find((user) => user.id === currentUser.id);
    if (selectedUser && currentPlayer) {
      const currentUserHandLength: number = currentPlayer.hand.length;
      const selectedUserHandLength: number = selectedUser.hand.length;
      let updatedSelectedId: string = selectedId;
      let updatedDiscardAmount: number = 0;
      if (selectedUserHandLength > currentUserHandLength) {
        updatedSelectedId = selectedUser.id;
        updatedDiscardAmount = selectedUserHandLength - currentUserHandLength;
      } else if (selectedUserHandLength < currentUserHandLength) {
        updatedSelectedId = currentPlayer.id;
        updatedDiscardAmount = currentUserHandLength - selectedUserHandLength;
      } else {
        updatedSelectedId = '';
        updatedDiscardAmount = 0;
      }
      onClose(updatedSelectedId, selectedColor, updatedDiscardAmount);
    }
  };

  const determineNextDrawCount = (userDrawSelectionsCopy: { [playerId: string]: number | undefined }) => {
    let nextNumber = 1;
    while (nextNumber < users.length + 1) {
      const userDrawSelectionsKeys = Object.keys(userDrawSelectionsCopy);
      let foundNextNumberInDrawSelectionsAlready = false;
      for (let i = 0; i < userDrawSelectionsKeys.length; i++) {
        if (userDrawSelectionsCopy[userDrawSelectionsKeys[i]] === nextNumber) {
          foundNextNumberInDrawSelectionsAlready = true
        }
      }
      if (!foundNextNumberInDrawSelectionsAlready) {
        setNextDrawCountOption(nextNumber);
        return;
      }
      nextNumber += 1;
    }
  }

  const toggleSelectionForId = (playerId: string) => {
    const userDrawSelectionsCopy: { [playerId: string]: number } = {}
    const userDrawSelectionsKeys = Object.keys(userDrawSelections);
    for (let i = 0; i < userDrawSelectionsKeys.length; i++) {
      userDrawSelectionsCopy[userDrawSelectionsKeys[i]] = userDrawSelections[userDrawSelectionsKeys[i]]
    }
    const selection = userDrawSelections[playerId];
    if (selection === 0) {
      userDrawSelectionsCopy[playerId] = nextDrawCountOption;
    } else {
      userDrawSelectionsCopy[playerId] = 0;
    }
    setUserDrawSelections(userDrawSelectionsCopy);
    determineNextDrawCount(userDrawSelectionsCopy);
  }

  const checkIfAllPlayersHaveADrawCount = (): boolean => {
    const userDrawSelectionsKeys = Object.keys(userDrawSelections);
    for (let i = 0; i < userDrawSelectionsKeys.length; i++) {
      if (userDrawSelections[userDrawSelectionsKeys[i]] === 0) {
        return false;
      }
    }
    return true;
  }

  const getModalConfig = () => {
    switch (type) {
      case "colorPicker":
        return {
          title: "Choose Color",
          icon: Palette,
          description: "Select the next color to play"
        };
      case "swap":
        return {
          title: "Swap Hands",
          icon: Shuffle,
          description: "Choose a player to swap hands with"
        };
      case "gettingHeated":
        return {
          title: "Getting Heated",
          icon: Flame,
          description: "Choose a player and color"
        };
      case "ghostPepper":
        return {
          title: "Ghost Pepper Spray",
          icon: Zap,
          description: "Choose players to draw cards and select a color"
        };
      case "fairPlay":
        return {
          title: "Fair Play",
          icon: Target,
          description: "Choose a player and color for fair play"
        };
      case "apocalypse":
        return {
          title: "Apocalypse",
          icon: Flame,
          description: "Choose the apocalypse color"
        };
      case "stackingTarget":
        return {
          title: "Getting Heated Stack",
          icon: Target,
          description: "Choose target player and color for your stacked Getting Heated"
        };
      default:
        return {
          title: "Action Required",
          icon: Users,
          description: ""
        };
    }
  };

  const renderColorSelection = () => (
    <motion.div className="color-selection" variants={itemVariants}>
      <h4>Select Color</h4>
      <div className="color-grid">
        {[
          { color: COLOR_GREEN, name: "Green", bg: "var(--green-500)" },
          { color: COLOR_BLUE, name: "Blue", bg: "var(--blue-500)" },
          { color: COLOR_YELLOW, name: "Yellow", bg: "var(--yellow-500)" },
          { color: COLOR_PURPLE, name: "Purple", bg: "var(--purple-500)" }
        ].map((colorOption) => (
          <motion.button
            key={colorOption.color}
            className={`color-option ${selectedColor === colorOption.color ? 'selected' : ''}`}
            style={{ backgroundColor: colorOption.bg }}
            onClick={() => setSelectedColor(colorOption.color)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {colorOption.name}
          </motion.button>
        ))}
      </div>
    </motion.div>
  );

  const renderPlayerSelection = () => (
    <motion.div className="player-selection" variants={itemVariants}>
      <h4>Select Player</h4>
      <div className="player-grid">
        {users.filter((player) => player.id !== currentUser.id).map((player, index) => (
          <motion.button
            key={player.id}
            className={`player-option ${selectedPlayerButton === index ? 'selected' : ''}`}
            onClick={() => {
              setSelectedId(player.id);
              setSelectedPlayerButton(index);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Users size={16} />
            <div className="player-info">
              <span className="player-name">{player.name}</span>
              <span className="player-cards">{player.hand.length} cards</span>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );

  const renderGhostPepperSelection = () => (
    <motion.div className="ghost-pepper-selection" variants={itemVariants}>
      <h4>Select Players to Draw Cards</h4>
      <div className="player-grid">
        {users.filter((player) => player.id !== currentUser.id).map((player) => {
          const drawCount = userDrawSelections[player.id];
          return (
            <motion.button
              key={player.id}
              className={`player-option ${drawCount > 0 ? 'selected' : ''}`}
              onClick={() => toggleSelectionForId(player.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Users size={16} />
              <div className="player-info">
                <span className="player-name">{player.name}</span>
                <span className="player-cards">
                  {drawCount > 0 ? `Draw ${drawCount}` : 'Not selected'}
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );

  const renderModalContent = () => {
    switch (type) {
      case "colorPicker":
      case "apocalypse":
        return renderColorSelection();
      case "swap":
      case "fairPlay":
        return (
          <>
            {renderPlayerSelection()}
            {(type === "fairPlay") && renderColorSelection()}
          </>
        );
      case "gettingHeated":
      case "stackingTarget":
        return (
          <>
            {renderPlayerSelection()}
            {renderColorSelection()}
          </>
        );
      case "ghostPepper":
        return (
          <>
            {renderGhostPepperSelection()}
            {renderColorSelection()}
          </>
        );
      default:
        return null;
    }
  };

  const isActionDisabled = () => {
    switch (type) {
      case "colorPicker":
      case "apocalypse":
        return !selectedColor;
      case "swap":
        return !selectedId;
      case "fairPlay":
      case "gettingHeated":
      case "stackingTarget":
        return !selectedId || !selectedColor;
      case "ghostPepper":
        return !checkIfAllPlayersHaveADrawCount() || !selectedColor;
      default:
        return false;
    }
  };

  const config = getModalConfig();
  const IconComponent = config.icon;

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay-modern"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="modal-content-modern"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div className="modal-header-modern" variants={itemVariants}>
            <div className="modal-title-section">
              <IconComponent className="modal-icon" />
              <div>
                <h2>{config.title}</h2>
                {config.description && <p>{config.description}</p>}
              </div>
            </div>
          </motion.div>

          <motion.div className="modal-body-modern" variants={itemVariants}>
            {renderModalContent()}
          </motion.div>

          <motion.div className="modal-footer-modern" variants={itemVariants}>
            <motion.button
              className={`btn-modern btn-primary ${isActionDisabled() ? 'disabled' : ''}`}
              onClick={onSet}
              disabled={isActionDisabled()}
              whileHover={!isActionDisabled() ? { scale: 1.05 } : {}}
              whileTap={!isActionDisabled() ? { scale: 0.95 } : {}}
            >
              Confirm
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ModernCardActionModal;
